import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "../../../components/AppIcon";
import FakeAppChrome from "../FakeAppChrome";
import TourPdfStage from "../TourPdfStage";
import { TOUR_DOCUMENT, TOUR_PREVIEW_LINES } from "../tourData";
import { useTourSamplePdf } from "../useTourSamplePdf";

const PaperFallback = () => (
  <div className="mx-auto max-w-[720px] rounded-sm border border-border bg-white text-zinc-900 shadow-elevation-sm p-8 sm:p-10">
    <div className="font-serif text-sm sm:text-[15px] leading-relaxed whitespace-pre-line">
      {TOUR_PREVIEW_LINES.join("\n")}
    </div>
  </div>
);

const PdfGeneratedScene = () => {
  const { pdfBlob, loadingPdf, loadError } = useTourSamplePdf();
  const [phase, setPhase] = useState("generating");

  useEffect(() => {
    const timer = window.setTimeout(() => setPhase("ready"), 1100);
    return () => window.clearTimeout(timer);
  }, []);

  const showViewer = phase === "ready" && !loadingPdf && pdfBlob && !loadError;
  const showFallback =
    phase === "ready" && !loadingPdf && (!pdfBlob || loadError);

  return (
    <FakeAppChrome activeNavId="generator">
      <div className="flex min-h-full flex-col">
        <header className="shrink-0 border-b border-border/80 bg-card px-4 py-3 sm:px-6">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-lg sm:text-xl font-bold truncate">
              Documento gerado
            </h1>
            <AnimatePresence mode="wait">
              {phase === "ready" ? (
                <motion.span
                  key="ready"
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="inline-flex items-center gap-1.5 rounded-md bg-success/10 px-2.5 py-1 text-xs font-semibold text-success"
                >
                  <Icon name="CheckCircle" size={14} />
                  PDF Gerado
                </motion.span>
              ) : (
                <motion.span
                  key="gen"
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary"
                >
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{
                      repeat: Infinity,
                      duration: 1,
                      ease: "linear",
                    }}
                    className="inline-flex"
                  >
                    <Icon name="Loader2" size={14} />
                  </motion.span>
                  Gerando PDF…
                </motion.span>
              )}
            </AnimatePresence>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 truncate">
            {TOUR_DOCUMENT.title}
          </p>
        </header>

        <div className="flex flex-col gap-2 p-3 sm:p-4 pb-6">
          {phase === "ready" ? (
            <div className="rounded-lg border border-success/30 bg-success/10 px-3 py-2 text-xs sm:text-sm text-foreground">
              PDF pronto. Você pode baixar, enviar por e-mail ou seguir para
              assinatura digital.
            </div>
          ) : (
            <div className="rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-xs sm:text-sm text-foreground">
              Montando o PDF profissional a partir do modelo preenchido…
            </div>
          )}

          <AnimatePresence mode="wait">
            {phase === "generating" ? (
              <motion.div
                key="pulse"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex min-h-[24rem] flex-col items-center justify-center gap-4 rounded-lg border border-border bg-muted/20 p-8"
              >
                <motion.div
                  className="h-20 w-14 rounded-sm border border-border bg-card shadow-soft-md"
                  animate={{ y: [0, -8, 0], rotate: [-2, 2, -2] }}
                  transition={{
                    repeat: Infinity,
                    duration: 1.4,
                    ease: "easeInOut",
                  }}
                />
                <div className="h-1.5 w-48 overflow-hidden rounded-full bg-muted">
                  <motion.div
                    className="h-full rounded-full bg-primary"
                    initial={{ width: "8%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                  />
                </div>
                <p className="text-sm text-muted-foreground">Gerando PDF…</p>
              </motion.div>
            ) : showViewer ? (
              <motion.div key="viewer" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <TourPdfStage
                  pdfBlob={pdfBlob}
                  loadingPdf={false}
                  loadError={null}
                  fields={[]}
                  viewOnly
                  currentPage={1}
                  totalPages={4}
                />
              </motion.div>
            ) : showFallback ? (
              <motion.div
                key="fallback"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-2"
              >
                <PaperFallback />
              </motion.div>
            ) : (
              <div
                key="load"
                className="flex min-h-[12rem] items-center justify-center text-sm text-muted-foreground"
              >
                Carregando prévia…
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </FakeAppChrome>
  );
};

export default PdfGeneratedScene;
